/**
 * Formats HTML string to ensure all anchor tag URLs are valid HTTP/HTTPS URLs
 * and adds security attributes (rel="noopener noreferrer" target="_blank")
 * @param string - The HTML string containing anchor tags
 * @returns The formatted HTML string with valid URLs and security attributes
 *
 * @example
 * makeAllUrlsValid('<a href="/example">Link</a>') // '<a href="http:///example" rel="noopener noreferrer" target="_blank">Link</a>'
 * makeAllUrlsValid('<a href="https://example.com">Link</a>') // '<a href="https://example.com" rel="noopener noreferrer" target="_blank">Link</a>'
 */
const ANCHOR_TAG_REGEX = /<a\b[^>]*>/gi;
const HREF_ATTRIBUTE_REGEX = /href\s*=\s*"([^"]*)"/i;
const SECURE_REL = 'rel="noopener noreferrer"';
const SECURE_TARGET = 'target="_blank"';

export function makeAllUrlsValid(html: string): string {
  return html.replace(ANCHOR_TAG_REGEX, (anchorTag) => {
    const hrefMatch = anchorTag.match(HREF_ATTRIBUTE_REGEX);

    if (!hrefMatch || typeof hrefMatch[1] !== "string") {
      return anchorTag;
    }

    const hrefValue = hrefMatch[1].trim();
    if (!hrefValue) {
      return anchorTag;
    }

    const normalizedHref = /^https?:\/\//i.test(hrefValue)
      ? hrefValue
      : `http://${hrefValue}`;

    let updatedTag = anchorTag.replace(
      hrefMatch[0],
      `href="${normalizedHref}"`,
    );

    updatedTag = ensureAttribute(updatedTag, "rel", SECURE_REL);
    updatedTag = ensureAttribute(updatedTag, "target", SECURE_TARGET);

    return updatedTag;
  });
}

function ensureAttribute(
  tag: string,
  attributeName: string,
  attributeString: string,
): string {
  const attributeRegex = new RegExp(`${attributeName}\\s*=\\s*"[^"]*"`, "i");

  if (attributeRegex.test(tag)) {
    return tag.replace(attributeRegex, attributeString);
  }

  return tag.replace(/(\s*\/?>)/, ` ${attributeString}$1`);
}
